import { connect } from 'react-redux';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';

import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmErknmPositionsSelectOptions,
  getKnmFormRef,
  getKnmOnSaveButtonClick,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldMark } from 'KnmPackage/MainModule/components/FieldMark/FieldMark';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import {
  failExecutionSelectOptions,
  successExecutionSelectOptions,
} from 'KnmPackage/MainModule/constants/agreementsExecutionSelectOptions';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { getAgreementExecutionValidate } from 'KnmErknmPackage/MainModule/common/getAgreementExecutionValidate';
import { IErknmFormAgreementExecution } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreementExecution';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import {
  getDecisionFormRef,
  getDecisionOnSaveButtonClick,
  getDecisionPositionsSelectOptions,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';

import styles from './ExecutionAgreementModal.module.scss';
import { AttachmentsInModals } from '../../AttachmentsInModals/AttachmentsInModals';

interface IExecutionAgreementModalProps {
  visible: boolean;
  areAllViolationCompleted: boolean;
  onClose: () => void;
  onCloseAll: () => void;
  name: string;
  executionModalInitType: ISelectOption<number> | undefined;
}

interface IExecutionAgreementModalStoreProps {
  positionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
  decisionPositionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
  onSaveButtonClickKnmAndPm: (() => void) | undefined;
  onSaveButtonClickDecision: (() => void) | undefined;
  formRefKnm: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
}

interface IForm extends IErknmFormAgreementExecution {}

const ExecutionAgreementModalWithoutStore = ({
  name,
  visible,
  onClose,
  onCloseAll,
  formRefKnm,
  decisionFormRef,
  executionModalInitType,
  positionsSelectOptions,
  areAllViolationCompleted,
  onSaveButtonClickDecision,
  onSaveButtonClickKnmAndPm,
  decisionPositionsSelectOptions,
}: IExecutionAgreementModalProps & IExecutionAgreementModalStoreProps) => {
  const formRef = useRef<FormApi<IForm, Partial<IForm>>>();
  const { isDecision } = useContext(AgreementsContext);

  const decisionSelectOptions = useMemo(() => {
    const options: ISelectOption<number>[] = [];

    if (areAllViolationCompleted) {
      options.push(successExecutionSelectOptions);
    }

    options.push(failExecutionSelectOptions);

    return options;
  }, [areAllViolationCompleted]);

  useEffect(() => {
    if (executionModalInitType) {
      formRef.current?.change('decision', executionModalInitType);
    }
  }, [executionModalInitType]);

  const handleClose = () => {
    onClose();
    formRef?.current?.reset();
  };

  const handleSave = useCallback(
    async (values: IForm) => {
      if (isDecision) {
        decisionFormRef?.current.change(`${name}.signed` as keyof IDecisionForm, false);
        // @ts-ignore
        decisionFormRef?.current.change(`${name}.execution` as keyof IDecisionForm, values);
        onSaveButtonClickDecision && onSaveButtonClickDecision();
      } else {
        formRefKnm?.current.change(`${name}.signed` as keyof IKnmForm, false);
        // @ts-ignore
        formRefKnm?.current.change(`${name}.execution` as keyof IKnmForm, values);
        onSaveButtonClickKnmAndPm && onSaveButtonClickKnmAndPm();
      }
      onCloseAll();
      formRef?.current?.reset();
    },
    [onCloseAll, formRef, handleClose],
  );

  const renderContentForm = useCallback(
    ({ handleSubmit, form, values }: FormRenderProps<IForm>) => {
      formRef.current = form;

      return (
        <>
          <ModalBody size="content" padding="small">
            <div className={styles.ContentWrapper}>
              <FieldBlock
                label={<FieldMark fieldName="decision" label="Решение об исполнении соглашения" />}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <SelectInputField
                  name={'decision'}
                  placeholder={'Решение'}
                  size="medium"
                  options={decisionSelectOptions}
                />
              </FieldBlock>
              {(values.decision?.value === 1 || values.decision?.value) && (
                <FieldBlock
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  label={<FieldMark fieldName="executionDate" label="Фактическая дата исполнения соглашения" />}
                >
                  <div>
                    <DatePickerField
                      name={`executionDate`}
                      placeholder={'Фактическая дата'}
                      size="medium"
                      minDate={new Date()}
                      className={styles.DatePicker}
                    />
                  </div>
                </FieldBlock>
              )}

              <AttachmentsInModals
                formRef={formRef}
                formats=".pdf"
                name="attachments"
                title="Представленные контролируемым лицом (учредителем) документы"
              />

              <FieldBlock label={'Решение принято'} margin="top">
                <FieldBlock
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  label={<FieldMark fieldName="fioSigner" label="ФИО принявшего решение" />}
                >
                  <TextInputField name={`fioSigner`} placeholder="ФИО" />
                </FieldBlock>
                <FieldBlock
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  label={<FieldMark fieldName="titleSigner" label="Должность лица, принявшего решение" />}
                >
                  <SelectInputField
                    name={`titleSigner`}
                    placeholder="Выберите должность"
                    options={isDecision ? decisionPositionsSelectOptions : positionsSelectOptions}
                    isSearchable
                    size="medium"
                    opensOnTop
                  />
                </FieldBlock>
              </FieldBlock>
            </div>
          </ModalBody>
          <ModalActions>
            <Button text="Отменить" size="medium" type="button" onClick={handleClose} />
            <Button text="Сохранить" color="primary" size="medium" type="button" onClick={handleSubmit} />
          </ModalActions>
        </>
      );
    },
    [positionsSelectOptions, handleClose],
  );

  return (
    <Modal visible={visible} onClose={handleClose}>
      <ModalHeader type="small" title="Исполнение соглашения" />
      <Form<IForm>
        render={renderContentForm}
        onSubmit={handleSave}
        validate={getAgreementExecutionValidate}
        mutators={{ ...arrayMutators }}
      />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IExecutionAgreementModalStoreProps => ({
  positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
  decisionPositionsSelectOptions: getDecisionPositionsSelectOptions(state),
  formRefKnm: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
  onSaveButtonClickKnmAndPm: getKnmOnSaveButtonClick(state),
  onSaveButtonClickDecision: getDecisionOnSaveButtonClick(state),
});

export const ExecutionAgreementModal = connect(mapStateToProps)(ExecutionAgreementModalWithoutStore);
